<script setup>
import { useDisplay } from "vuetify";

definePage({
  name: "Base",
  meta: {
    layout: "default",
    // public: true,
    requiresAuth: true,
    title: "Base",
    description: "Manage battles, loadouts, comms and rewards from the Nexus base.",
  },
});

const { mobile, mdAndUp, lgAndUp } = useDisplay();
const router = useRouter();
const authStore = useAuthStore();
const commsBlockRef = ref();
const createMomentBtnRef = ref();
const parentCommsBlockRef = ref();

const leftValue = ref("0px");
const bottomValue = ref("40px");

const ALIGN_EVENT = "battle-terminals-align";
let alignmentFrameId = null;
let terminalMarginApplied = 0;
let miniHubMarginApplied = 0;
let loadoutMarginApplied = 0;
let commsMarginApplied = 0;
let alignmentObserver;

onMounted(async () => {
  if (mobile.value) {
    await router.push("/base/mobile-loadout");
    return;
  }

  await nextTick();
  updateChildPosition();
  scheduleAlignment();
  observeAlignmentTargets();

  window.addEventListener("resize", updateChildPosition);
  window.addEventListener("scroll", updateChildPosition);
  window.addEventListener("resize", scheduleAlignment);
  window.addEventListener(ALIGN_EVENT, scheduleAlignment);
});

const updateChildPosition = () => {
  nextTick(() => {
    const footer = document.querySelector("#footer");
    const btn = createMomentBtnRef.value;
    if (!footer || !btn) return;
    const footerRect = footer.getBoundingClientRect();
    const windowHeight = window.innerHeight;
    if (footerRect.top < windowHeight) {
      const overlap = windowHeight - footerRect.top;
      bottomValue.value = `${overlap + 20}px`;
    } else {
      bottomValue.value = "60px";
    }
    if (parentCommsBlockRef.value) {
      const rect = parentCommsBlockRef.value.getBoundingClientRect();
      if (rect.right && rect.width > 0) {
        leftValue.value = `${rect.right}px`;
      }
    }
  });
};

const resetAlignment = () => {
  const terminalsEl = document.getElementById("battle-terminals");
  const miniHubEl = document.getElementById("my-mini-hub");
  const loadoutEl = document.getElementById("loadout-block");
  const commsEl = document.getElementById("comms-block");

  if (terminalsEl && terminalMarginApplied !== 0) {
    terminalsEl.style.marginTop = "";
    terminalMarginApplied = 0;
  }

  if (miniHubEl && miniHubMarginApplied !== 0) {
    miniHubEl.style.marginTop = "";
    miniHubMarginApplied = 0;
  }

  if (loadoutEl && loadoutMarginApplied !== 0) {
    loadoutEl.style.marginTop = "";
    loadoutMarginApplied = 0;
  }

  if (commsEl && commsMarginApplied !== 0) {
    commsEl.style.marginTop = "";
    commsMarginApplied = 0;
  }
};

const applyAlignment = () => {
  if (!lgAndUp.value) {
    resetAlignment();
    return;
  }

  const terminalsEl = document.getElementById("battle-terminals");
  const miniHubEl = document.getElementById("my-mini-hub");
  const headerEl = terminalsEl?.querySelector("[data-terminal-header]");
  const loadoutEl = document.getElementById("loadout-block");
  const commsEl = document.getElementById("comms-block");

  if (!terminalsEl || !miniHubEl || !headerEl) {
    if (terminalsEl && terminalMarginApplied !== 0) {
      terminalsEl.style.marginTop = "";
    }
    if (miniHubEl && miniHubMarginApplied !== 0) {
      miniHubEl.style.marginTop = "";
    }
    terminalMarginApplied = 0;
    miniHubMarginApplied = 0;
  } else {
    const terminalStyles = getComputedStyle(terminalsEl);
    const miniHubStyles = getComputedStyle(miniHubEl);
    const currentTerminalMargin =
      parseFloat(terminalStyles.marginTop || "0") || 0;
    const currentMiniHubMargin =
      parseFloat(miniHubStyles.marginTop || "0") || 0;

    const terminalTop =
      terminalsEl.getBoundingClientRect().top - currentTerminalMargin;
    const headerStyles = getComputedStyle(headerEl);
    const headerOffset = parseFloat(headerStyles.marginBottom || "0");
    const safeHeaderOffset = Number.isNaN(headerOffset) ? 0 : headerOffset;
    const listTop = terminalTop + headerEl.offsetHeight + safeHeaderOffset;

    const miniHubTop =
      miniHubEl.getBoundingClientRect().top - currentMiniHubMargin;
    const diff = Math.round(miniHubTop - listTop);

    let newTerminalMargin = 0;
    let newMiniHubMargin = 0;

    if (diff > 0) {
      newTerminalMargin = diff;
    } else if (diff < 0) {
      newMiniHubMargin = Math.abs(diff);
    }

    if (newTerminalMargin !== terminalMarginApplied) {
      terminalsEl.style.marginTop = newTerminalMargin
        ? `${newTerminalMargin}px`
        : "";
      terminalMarginApplied = newTerminalMargin;
    }

    if (newMiniHubMargin !== miniHubMarginApplied) {
      miniHubEl.style.marginTop = newMiniHubMargin
        ? `${newMiniHubMargin}px`
        : "";
      miniHubMarginApplied = newMiniHubMargin;
    }
  }

  if (!loadoutEl || !commsEl) {
    if (loadoutEl && loadoutMarginApplied !== 0) {
      loadoutEl.style.marginTop = "";
      loadoutMarginApplied = 0;
    }
    if (commsEl && commsMarginApplied !== 0) {
      commsEl.style.marginTop = "";
      commsMarginApplied = 0;
    }
    return;
  }

  const loadoutStyles = getComputedStyle(loadoutEl);
  const commsStyles = getComputedStyle(commsEl);
  const currentLoadoutMargin = parseFloat(loadoutStyles.marginTop || "0") || 0;
  const currentCommsMargin = parseFloat(commsStyles.marginTop || "0") || 0;

  const loadoutTop =
    loadoutEl.getBoundingClientRect().top - currentLoadoutMargin;
  const commsTop = commsEl.getBoundingClientRect().top - currentCommsMargin;
  const diffLoadoutComms = Math.round(loadoutTop - commsTop);

  let newLoadoutMargin = 0;
  let newCommsMargin = 0;

  if (diffLoadoutComms > 0) {
    newCommsMargin = diffLoadoutComms;
  } else if (diffLoadoutComms < 0) {
    newLoadoutMargin = Math.abs(diffLoadoutComms);
  }

  if (newLoadoutMargin !== loadoutMarginApplied) {
    loadoutEl.style.marginTop = newLoadoutMargin ? `${newLoadoutMargin}px` : "";
    loadoutMarginApplied = newLoadoutMargin;
  }

  if (newCommsMargin !== commsMarginApplied) {
    commsEl.style.marginTop = newCommsMargin ? `${newCommsMargin}px` : "";
    commsMarginApplied = newCommsMargin;
  }
};

const scheduleAlignment = () => {
  if (alignmentFrameId !== null) {
    return;
  }

  alignmentFrameId = requestAnimationFrame(() => {
    alignmentFrameId = null;
    applyAlignment();
  });
};

const observeAlignmentTargets = () => {
  if (typeof window === "undefined" || !("ResizeObserver" in window)) {
    return;
  }

  const elements = [
    document.getElementById("battle-terminals"),
    document.getElementById("my-mini-hub"),
    document.getElementById("loadout-block"),
    document.getElementById("comms-block"),
  ].filter(Boolean);

  if (!elements.length) {
    alignmentObserver?.disconnect();
    alignmentObserver = undefined;
    return;
  }

  alignmentObserver?.disconnect();
  alignmentObserver = new ResizeObserver(() => {
    scheduleAlignment();
  });

  elements.forEach((el) => alignmentObserver.observe(el));
};

const reloadComms = async () => {
  commsBlockRef.value.reloadComms();
};

watch(
  lgAndUp,
  () => {
    if (!lgAndUp.value) {
      resetAlignment();
      alignmentObserver?.disconnect();
      alignmentObserver = undefined;
      return;
    }

    scheduleAlignment();
    observeAlignmentTargets();
  },
  { flush: "post" }
);

onUnmounted(() => {
  window.removeEventListener("resize", updateChildPosition);
  window.removeEventListener("scroll", updateChildPosition);
  window.removeEventListener("resize", scheduleAlignment);
  window.removeEventListener(ALIGN_EVENT, scheduleAlignment);

  resetAlignment();

  if (alignmentFrameId !== null) {
    cancelAnimationFrame(alignmentFrameId);
    alignmentFrameId = null;
  }

  alignmentObserver?.disconnect();
});
</script>

<template>
  <!-- StarrySky as fixed background -->
  <StarrySky class="" />

  <!-- Main Content -->
  <div class="tw-page-container tw-min-h-screen tw-relative tw-z-10">
    <v-container>
      <div
        class="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 xl:tw-grid-cols-[9fr_11fr] tw-gap-4"
      >
        <div class="">
          <my-profile-block />

          <loadout-block id="loadout-block" />
        </div>
        <div
          class="tw-gap-4 tw-relative tw-overflow-hidden"
          ref="parentCommsBlockRef"
        >
          <battle-block class="tw-mb-4" />
          <comms-block ref="commsBlockRef" id="comms-block" />
          <create-moment-btn
            ref="createMomentBtnRef"
            class="tw-fixed tw-z-[1]"
            @onCreateMomentSuccess="reloadComms"
            :style="{
              '--left-value': leftValue,
              '--bottom-value': bottomValue,
            }"
          />
        </div>
      </div>
    </v-container>
  </div>
</template>

<style>
.btn-create-moment {
  left: calc(var(--left-value) - 72px);
  /* bottom: max(10vh, 70px); */
  bottom: var(--bottom-value);
}

.tw-page-container {
  background-color: transparent;
}
</style>
